/**
 * Browser turn-based voice client for ElevenLabs (with OpenAI tts-1 fallback).
 *
 * Two recording modes share the same upload + playback logic:
 *   - recordTurnAuto()    — VAD-driven hands-free turn (default).
 *   - recordTurnManual()  — manual record/stop, no VAD (push-to-talk).
 *
 * Per turn:
 *   1. Capture audio (MediaRecorder, audio/webm;codecs=opus when available).
 *   2. POST to /api/voice/turn (multipart).
 *   3. Backend streams MP3 audio + an in-band trailer:
 *         <audio bytes> + REPLY_TRAILER_SENTINEL + base64-url(reply text)
 *      We parse out the trailer client-side, route audio bytes to a
 *      MediaSource (or Blob fallback), and surface reply text via onTurn.
 *   4. Headers carry transcript (X-Transcript) and engine (X-Voice-Engine).
 *   5. After audio finishes, hands-free auto-resumes listening; push-to-talk
 *      stays idle until the user holds again.
 *
 * Lifecycle states emitted via onState:
 *   "idle" | "listening" | "recording" | "uploading" | "thinking"
 *   | "speaking" | "ended" | "error"
 */

const VAD_DEFAULTS = {
  silenceThresh: 0.015,   // RMS gate
  silenceMs: 550,          // sustained silence to auto-end the turn
  maxRecordMs: 30_000,
  preSpeechMaxMs: 8_000,   // give up if user hasn't said anything in this window
};

const SNIPPET_LEN = 240;

// In-band trailer marker — must match REPLY_TRAILER_SENTINEL on the server.
const REPLY_SENTINEL = new TextEncoder().encode("\n--JESUS_REPLY_v1--\n");

function uuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "sess-" + Math.random().toString(36).slice(2) + "-" + Date.now();
}

function pickRecorderMime() {
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
    "",
  ];
  for (const m of candidates) {
    try {
      if (m === "" || (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(m))) {
        return m;
      }
    } catch { /* ignore */ }
  }
  return "";
}

function b64uDecode(s) {
  if (!s) return "";
  let str = s.replace(/-/g, "+").replace(/_/g, "/");
  while (str.length % 4) str += "=";
  try {
    return decodeURIComponent(escape(atob(str)));
  } catch {
    try { return atob(str); } catch { return ""; }
  }
}

function bytesIndexOf(haystack, needle, start = 0) {
  // Minimal substring search for Uint8Array — ok for short needles.
  outer: for (let i = start; i <= haystack.length - needle.length; i++) {
    for (let j = 0; j < needle.length; j++) {
      if (haystack[i + j] !== needle[j]) continue outer;
    }
    return i;
  }
  return -1;
}

function concatU8(a, b) {
  const out = new Uint8Array(a.length + b.length);
  out.set(a, 0);
  out.set(b, a.length);
  return out;
}

export class ElevenLabsVoiceCall {
  constructor({ apiBase, mode = "auto", onState, onError, onTurn, vad = {} }) {
    this.apiBase = apiBase;
    this.mode = mode === "manual" ? "manual" : "auto";
    this.onState = onState || (() => {});
    this.onError = onError || (() => {});
    this.onTurn = onTurn || (() => {});
    this.vad = { ...VAD_DEFAULTS, ...vad };

    this.sessionId = uuid();
    this.stream = null;
    this.recorder = null;
    this.recorderMime = "";
    this.audioCtx = null;
    this.analyser = null;
    this.audioEl = null;
    this.vadStop = null;
    this.recordTimeout = null;

    this.state = "idle";
    this._destroyed = false;
  }

  _setState(s) {
    if (this._destroyed) return;
    this.state = s;
    this.onState(s);
  }

  _emitError(stage, err, extra = {}) {
    const message = explainStage(stage, err, extra);
    const detail = {
      stage,
      headline: message,
      message,
      raw: String((err && err.message) || err || ""),
      ...extra,
    };
    // eslint-disable-next-line no-console
    console.error("[el voice error]", detail);
    this.onError(detail);
    this._setState("error");
  }

  /** Open the mic + audio output channels. Does NOT begin a turn yet. */
  async start() {
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      this._emitError("mic", err);
      return;
    }
    this.audioEl = document.createElement("audio");
    this.audioEl.autoplay = true;
    this.audioEl.setAttribute("data-testid", "remote-audio");
    document.body.appendChild(this.audioEl);

    if (this.mode === "auto") {
      this._setState("listening");
      this._beginAutoTurn();
    } else {
      // Push-to-talk waits for the user to hold the button.
      this._setState("idle");
    }
  }

  /** Hands-free: end the current recording manually. */
  endTurn() {
    if (this.state === "recording" || this.state === "listening") {
      this._stopRecording();
    }
  }

  /** Push-to-talk: start a fresh manual recording. */
  recordTurnManual() {
    if (this._destroyed || !this.stream) return false;
    if (this.state !== "idle" && this.state !== "ended") return false;
    this._beginManualTurn();
    return true;
  }

  /**
   * Push-to-talk: stop manual recording. If the recording was shorter than
   * `minMs` (default 300), the audio is discarded and onState fires "idle"
   * so the UI can show a "Hold a bit longer" toast without uploading silence.
   */
  stopRecordTurnManual({ minMs = 300 } = {}) {
    if (this.state !== "recording") return;
    const elapsed = performance.now() - (this._manualStart || 0);
    if (elapsed < minMs) {
      this._abortManualTurn();
      return { tooShort: true, elapsed };
    }
    this._stopRecording();
    return { tooShort: false, elapsed };
  }

  /** Tear everything down — releases mic and stops any pending playback. */
  end() {
    this._destroyed = true;
    try { this.recorder?.state !== "inactive" && this.recorder?.stop(); } catch { /* noop */ }
    try { this.vadStop?.(); } catch { /* noop */ }
    try { clearTimeout(this.recordTimeout); } catch { /* noop */ }
    try { this.audioCtx?.close(); } catch { /* noop */ }
    try { this.stream?.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
    if (this.audioEl) {
      try { this.audioEl.pause(); } catch { /* noop */ }
      try { this.audioEl.src = ""; } catch { /* noop */ }
      try { this.audioEl.remove(); } catch { /* noop */ }
    }
    this.recorder = null;
    this.stream = null;
    this.audioCtx = null;
    this.analyser = null;
    this.audioEl = null;
    this.onState("ended");
  }

  // ------------------------------------------------------------------------
  // Auto (VAD) recording
  // ------------------------------------------------------------------------
  _beginAutoTurn() {
    if (this._destroyed || !this.stream) return;
    this._setState("listening");
    if (!this._initRecorder({
      onStop: (blob, mime) => this._uploadAndPlay(blob, mime, /*resumeOnEnd*/ true),
    })) return;
    this.recorder.start();
    this._setState("recording");
    this._startVAD();
    this.recordTimeout = setTimeout(() => this._stopRecording(), this.vad.maxRecordMs);
  }

  // ------------------------------------------------------------------------
  // Manual (push-to-talk) recording
  // ------------------------------------------------------------------------
  _beginManualTurn() {
    if (this._destroyed || !this.stream) return;
    if (!this._initRecorder({
      onStop: (blob, mime) => this._uploadAndPlay(blob, mime, /*resumeOnEnd*/ false),
    })) return;
    this._manualStart = performance.now();
    this._manualAborted = false;
    this.recorder.start();
    this._setState("recording");
    // No VAD; cap by maxRecordMs only.
    this.recordTimeout = setTimeout(() => this._stopRecording(), this.vad.maxRecordMs);
  }

  _abortManualTurn() {
    this._manualAborted = true;
    try { this.recorder && this.recorder.state !== "inactive" && this.recorder.stop(); } catch { /* noop */ }
    clearTimeout(this.recordTimeout);
    this._setState("idle");
  }

  // ------------------------------------------------------------------------
  // Recorder + VAD plumbing (shared)
  // ------------------------------------------------------------------------
  _initRecorder({ onStop }) {
    this.recorderMime = pickRecorderMime();
    try {
      this.recorder = this.recorderMime
        ? new MediaRecorder(this.stream, { mimeType: this.recorderMime })
        : new MediaRecorder(this.stream);
    } catch (err) {
      this._emitError("recorder", err);
      return false;
    }

    const chunks = [];
    this.recorder.ondataavailable = (ev) => {
      if (ev.data && ev.data.size > 0) chunks.push(ev.data);
    };
    this.recorder.onstop = async () => {
      try { this.vadStop?.(); } catch { /* noop */ }
      clearTimeout(this.recordTimeout);
      if (this._manualAborted) {
        this._manualAborted = false;
        return;
      }
      const mime = this.recorder?.mimeType || this.recorderMime || "audio/webm";
      const blob = new Blob(chunks, { type: mime });
      if (blob.size === 0) {
        // Nothing captured — restart hands-free or sit idle (PTT).
        if (this.mode === "auto" && !this._destroyed) this._beginAutoTurn();
        else this._setState("idle");
        return;
      }
      await onStop(blob, mime);
    };
    return true;
  }

  _stopRecording() {
    try {
      if (this.recorder && this.recorder.state !== "inactive") this.recorder.stop();
    } catch { /* noop */ }
  }

  _startVAD() {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx || !this.stream) return;
      this.audioCtx = new Ctx();
      const src = this.audioCtx.createMediaStreamSource(this.stream);
      this.analyser = this.audioCtx.createAnalyser();
      this.analyser.fftSize = 1024;
      src.connect(this.analyser);

      const buf = new Float32Array(this.analyser.fftSize);
      const startedAt = performance.now();
      let hadSpeech = false;
      let silenceStart = null;
      let stopped = false;

      const tick = () => {
        if (stopped || this._destroyed) return;
        this.analyser.getFloatTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
        const rms = Math.sqrt(sum / buf.length);

        if (rms > this.vad.silenceThresh) {
          hadSpeech = true;
          silenceStart = null;
        } else if (hadSpeech) {
          if (silenceStart === null) silenceStart = performance.now();
          else if (performance.now() - silenceStart >= this.vad.silenceMs) {
            stopped = true;
            this._stopRecording();
            return;
          }
        } else if (performance.now() - startedAt > this.vad.preSpeechMaxMs) {
          stopped = true;
          this._stopRecording();
          return;
        }
        requestAnimationFrame(tick);
      };
      this.vadStop = () => { stopped = true; };
      requestAnimationFrame(tick);
    } catch (err) {
      // VAD is best-effort; recorder will still hit maxRecordMs cap.
      // eslint-disable-next-line no-console
      console.warn("[vad] disabled:", err?.message || err);
    }
  }

  // ------------------------------------------------------------------------
  // Upload + streaming playback (shared)
  // ------------------------------------------------------------------------
  async _uploadAndPlay(blob, mime, resumeOnEnd) {
    if (this._destroyed) return;
    this._setState("uploading");

    const fd = new FormData();
    const ext = mime.includes("mp4") ? "mp4" : (mime.includes("webm") ? "webm" : "audio");
    fd.append("audio", blob, `turn.${ext}`);
    fd.append("session_id", this.sessionId);

    let resp;
    try {
      resp = await fetch(`${this.apiBase}/api/voice/turn`, { method: "POST", body: fd });
    } catch (err) {
      this._emitError("network", err);
      this._tryResume(resumeOnEnd);
      return;
    }

    if (resp.status === 429) {
      const retryAfter = resp.headers.get("Retry-After");
      this._emitError("rate_limit", new Error("rate_limit"), {
        httpStatus: 429,
        retryAfter,
      });
      this._tryResume(resumeOnEnd);
      return;
    }

    if (!resp.ok) {
      let bodyText = "";
      try { bodyText = await resp.text(); } catch { /* ignore */ }
      let parsed = null;
      try { parsed = JSON.parse(bodyText); } catch { /* not JSON */ }
      const e = parsed?.detail?.error || {};
      this._emitError("turn", new Error(e.message || `HTTP ${resp.status}`), {
        httpStatus: resp.status,
        code: e.code ?? null,
        stageUpstream: e.stage ?? null,
        snippet: bodyText.slice(0, SNIPPET_LEN),
      });
      this._tryResume(resumeOnEnd);
      return;
    }

    const transcript = b64uDecode(resp.headers.get("X-Transcript") || "");
    const engine = resp.headers.get("X-Voice-Engine") || "elevenlabs";
    // X-Reply is now delivered via in-band trailer (extracted in _streamPlay).

    this._setState("thinking");
    const reply = await this._streamPlay(resp);
    this.onTurn({ transcript, reply, engine });
    if (!this._destroyed) this._tryResume(resumeOnEnd);
  }

  _tryResume(resumeOnEnd) {
    if (this._destroyed) return;
    if (this.mode === "auto" && resumeOnEnd) this._beginAutoTurn();
    else this._setState("idle");
  }

  /**
   * Stream the response body, separating audio bytes (sent to <audio>)
   * from the trailing reply text (after REPLY_SENTINEL).
   * Uses MediaSource for progressive playback when supported, otherwise
   * Blob-based playback.
   * Returns the parsed reply text (may be empty if trailer absent).
   */
  async _streamPlay(response) {
    const mse = window.MediaSource && MediaSource.isTypeSupported("audio/mpeg");
    if (mse && response.body) {
      return await this._playWithMSE(response);
    }
    return await this._playWithBlob(response);
  }

  async _playWithMSE(response) {
    return await new Promise((resolve) => {
      const ms = new MediaSource();
      this.audioEl.src = URL.createObjectURL(ms);

      const reader = response.body.getReader();
      const queue = [];
      let sb = null;
      let appending = false;
      let upstreamDone = false;
      let started = false;
      let replyBytes = new Uint8Array(0);
      let foundSentinel = false;
      let tail = new Uint8Array(0);

      const trySpeakingState = () => {
        if (!started) {
          started = true;
          this._setState("speaking");
        }
      };

      const finalize = () => {
        const text = new TextDecoder().decode(replyBytes).trim();
        const reply = text ? b64uDecode(text) : "";
        resolve(reply);
      };

      const drain = () => {
        if (!sb || sb.updating) return;
        if (queue.length) {
          appending = true;
          try { sb.appendBuffer(queue.shift()); } catch (err) {
            // eslint-disable-next-line no-console
            console.warn("[mse] appendBuffer error", err);
          }
        } else if (upstreamDone) {
          appending = false;
          try { if (ms.readyState === "open") ms.endOfStream(); } catch { /* noop */ }
        } else {
          appending = false;
        }
      };

      const enqueueAudio = (bytes) => {
        if (!bytes || bytes.length === 0) return;
        queue.push(bytes);
        if (!appending) drain();
        if (!started) {
          this.audioEl.play().catch(() => { /* autoplay block — user gesture handles it */ });
        }
      };

      const onEnded = () => finalize();

      ms.addEventListener("sourceopen", () => {
        try {
          sb = ms.addSourceBuffer("audio/mpeg");
        } catch (err) {
          // Fallback to blob playback if MSE rejects audio/mpeg at runtime.
          // eslint-disable-next-line no-console
          console.warn("[mse] sourceBuffer rejected, falling back to blob:", err?.message);
          this._playWithBlob(response).then(resolve);
          return;
        }
        sb.addEventListener("updateend", drain);
        this.audioEl.addEventListener("ended", onEnded, { once: true });
        this.audioEl.addEventListener("playing", trySpeakingState, { once: true });

        const pump = async () => {
          try {
            // eslint-disable-next-line no-constant-condition
            while (true) {
              const { value, done } = await reader.read();
              if (done) {
                if (!foundSentinel && tail.length) enqueueAudio(tail);
                upstreamDone = true;
                drain();
                break;
              }
              if (!value || !value.length) continue;

              if (foundSentinel) {
                replyBytes = concatU8(replyBytes, value);
                continue;
              }

              const combined = concatU8(tail, value);
              const idx = bytesIndexOf(combined, REPLY_SENTINEL);
              if (idx >= 0) {
                foundSentinel = true;
                if (idx > 0) enqueueAudio(combined.slice(0, idx));
                replyBytes = combined.slice(idx + REPLY_SENTINEL.length);
                upstreamDone = true; // no more audio after sentinel
                drain();
                continue;
              }
              // Hold back the last (sentinelLen - 1) bytes for the next chunk
              // so a sentinel split across the boundary is still detected.
              const cut = Math.max(0, combined.length - (REPLY_SENTINEL.length - 1));
              enqueueAudio(combined.slice(0, cut));
              tail = combined.slice(cut);
            }
          } catch (err) {
            // eslint-disable-next-line no-console
            console.warn("[mse] reader error", err);
            upstreamDone = true;
            drain();
          }
        };
        pump();
      }, { once: true });
    });
  }

  async _playWithBlob(response) {
    let bytes;
    try {
      const buf = await response.arrayBuffer();
      bytes = new Uint8Array(buf);
    } catch (err) {
      this._emitError("playback", err);
      return "";
    }
    if (this._destroyed || !this.audioEl) return "";

    const idx = bytesIndexOf(bytes, REPLY_SENTINEL);
    let audioBytes = bytes;
    let reply = "";
    if (idx >= 0) {
      audioBytes = bytes.slice(0, idx);
      const trailer = new TextDecoder().decode(bytes.slice(idx + REPLY_SENTINEL.length)).trim();
      reply = trailer ? b64uDecode(trailer) : "";
    }
    const blob = new Blob([audioBytes], { type: "audio/mpeg" });
    const url = URL.createObjectURL(blob);
    this.audioEl.src = url;
    return await new Promise((resolve) => {
      const cleanup = () => {
        try { URL.revokeObjectURL(url); } catch { /* noop */ }
        resolve(reply);
      };
      this.audioEl.addEventListener("ended", cleanup, { once: true });
      this.audioEl.addEventListener("error", cleanup, { once: true });
      this.audioEl.play().then(() => this._setState("speaking")).catch(() => cleanup());
    });
  }
}

export function explainStage(stage, err, extra = {}) {
  const name = err?.name || "";
  if (stage === "mic") {
    if (name === "NotAllowedError" || name === "SecurityError") {
      return "Microphone access is required for voice calls — please allow mic in your browser settings and try again.";
    }
    if (name === "NotFoundError" || name === "OverconstrainedError") {
      return "No microphone was detected. Please connect a mic and try again.";
    }
    return "Could not access the microphone. Please check your browser permissions.";
  }
  if (stage === "recorder") return "This browser doesn't support audio recording.";
  if (stage === "network") return "Couldn't reach the server. Please check your connection.";
  if (stage === "rate_limit") {
    const after = extra.retryAfter ? ` (try again in ${extra.retryAfter}s)` : "";
    return `Please wait a moment before calling again${after}.`;
  }
  if (stage === "turn") {
    const us = extra.stageUpstream;
    const code = extra.code;
    if (us === "stt" && code === "no_speech") return "I couldn't hear you clearly. Try again a little louder.";
    if (us === "stt") return "Couldn't transcribe what you said. Please try again.";
    if (us === "llm") return "The conversation engine had a hiccup. Please try again.";
    if (us === "tts") return "The voice service is unavailable. Please try again in a moment.";
    if (extra.httpStatus) return `Server error (HTTP ${extra.httpStatus}).`;
    return err?.message || "Something went wrong on this turn.";
  }
  if (stage === "playback") return "Couldn't play the audio response.";
  return err?.message || "Something went wrong.";
}
